import { EmbedDefinition } from '../../../services/embed/embedService';
import { HtmlEditService } from '../../../services/html/htmlEditService';
import { ContentEditableElement } from '../contentEditableEditor';
import {
  ContentEditableEditorDecoration,
  ContentEditableEditorPlugin,
  TextNodeAndCursorPosition,
} from './contentEditableEditorPlugin';

export class ContentEditableEditorPluginCollection<
  T extends ContentEditableEditorPlugin = ContentEditableEditorPlugin
> implements Required<ContentEditableEditorPlugin> {
  constructor(plugins: ReadonlyArray<T> = []) {
    this.plugins = plugins;
  }

  readonly plugins: ReadonlyArray<T>;

  handleDataTransfer(
    dataTransfer: DataTransfer,
    editService: HtmlEditService,
    element: ContentEditableElement,
  ): boolean {
    return this.plugins.some(
      plugin => !!plugin.handleDataTransfer && plugin.handleDataTransfer(dataTransfer, editService, element),
    );
  }

  populateDataTransfer(dataTransfer: DataTransfer, element: ContentEditableElement): boolean {
    return this.plugins.some(
      plugin => !!plugin.populateDataTransfer && plugin.populateDataTransfer(dataTransfer, element),
    );
  }

  preProcessInsertedHtml(dirtyHtml: string, document?: Document): string {
    return this.plugins.reduce(
      (html, plugin) =>
        plugin.preProcessInsertedHtml ? plugin.preProcessInsertedHtml(html, document) : html,
      dirtyHtml,
    );
  }

  postProcessInsertedHtml(cleanHtml: string): string {
    return this.plugins.reduce(
      (html, plugin) => (plugin.postProcessInsertedHtml ? plugin.postProcessInsertedHtml(html) : html),
      cleanHtml,
    );
  }

  processText(container: Element, textNodeAndCursorPositions: Iterable<TextNodeAndCursorPosition>): void {
    this.plugins.forEach(
      plugin => !!plugin.processText && plugin.processText(container, textNodeAndCursorPositions),
    );
  }

  processTree(container: Element): void {
    this.plugins.forEach(plugin => !!plugin.processTree && plugin.processTree(container));
  }

  processEmbeds(container: Element): ReadonlyArray<EmbedDefinition> {
    return this.plugins.reduce((accumulatedDefinitions, plugin) => {
      if (!plugin.processEmbeds) {
        return accumulatedDefinitions;
      }

      const pluginDefinitions = plugin.processEmbeds(container);
      if (pluginDefinitions.length === 0) {
        return accumulatedDefinitions;
      }

      return accumulatedDefinitions.concat(pluginDefinitions);
    }, [] as EmbedDefinition[]);
  }

  get hasDecorateText(): boolean {
    return this.plugins.some(plugin => !!plugin.decorateText);
  }

  decorateText(text: string): ReadonlyArray<ContentEditableEditorDecoration> {
    return this.plugins.reduce((accumulatedDecorations, plugin) => {
      if (!plugin.decorateText) {
        return accumulatedDecorations;
      }

      const pluginDecorations = plugin.decorateText(text);
      if (pluginDecorations.length === 0) {
        return accumulatedDecorations;
      }

      return accumulatedDecorations.concat(pluginDecorations);
    }, [] as ContentEditableEditorDecoration[]);
  }
}
